import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { ColorModeContext } from "../App";

const NavBar = ({ isLoggedIn, isAdmin, onLogout }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { toggleColorMode, colorMode } = useContext(ColorModeContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isEmbedded = searchParams.get("embed") === "true";

  if (isEmbedded) {
    return null;
  }

  const navLinks = [
    { label: "Chatbot", path: "/chatbot" },
    { label: "About", path: "/about" },
    { label: "How it works", path: "/how-it-works" },
    { label: "WhatsApp", path: "/whatsapp" },
    { label: "Privacy", path: "/privacy" },
  ];

  if (isLoggedIn) {
    navLinks.push({ label: "Dashboard", path: "/dashboard" });
    if (isAdmin) navLinks.push({ label: "Admin", path: "/platform-admin" });
  } else {
    navLinks.push({ label: "Login", path: "/login" });
  }

  const isActive = (path) => location.pathname === path;

  const darkModeToggle = (
    <IconButton
      onClick={toggleColorMode}
      size="small"
      aria-label={colorMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
    >
      {colorMode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
    </IconButton>
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.custom.mintGreen,
          height: 60,
          width: "100%",
          display: "flex",
          alignItems: "center",
          px: theme.spacing(1.5),
        }}
      >
        <IconButton
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation menu"
          sx={{ color: theme.palette.custom.darkCharcoal }}
        >
          <MenuIcon />
        </IconButton>
        <Typography
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            textDecoration: "none",
            fontSize: 16,
            fontWeight: 700,
            color: theme.palette.custom.darkCharcoal,
            textAlign: "center",
          }}
        >
          Rightsbot
        </Typography>
        {darkModeToggle}
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 240, pt: theme.spacing(2) }} role="presentation">
            <List>
              {navLinks.map((link) => (
                <ListItemButton
                  key={link.label}
                  component={Link}
                  to={link.path}
                  selected={isActive(link.path)}
                  onClick={() => setDrawerOpen(false)}
                >
                  <ListItemText primary={link.label} />
                </ListItemButton>
              ))}
              {isLoggedIn && (
                <ListItemButton
                  onClick={() => {
                    setDrawerOpen(false);
                    onLogout();
                  }}
                >
                  <ListItemText primary="Logout" />
                </ListItemButton>
              )}
            </List>
          </Box>
        </Drawer>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        component={Link}
        to="/"
        sx={{
          textDecoration: "none",
          backgroundColor: theme.palette.custom.mintGreen,
          height: 72,
          width: "100%",
          display: "flex",
          alignItems: "center",
          px: theme.spacing(2),
        }}
      >
        <Typography
          variant="h6"
          sx={{
            flexGrow: 1,
            fontSize: 18,
            fontWeight: 700,
            color: theme.palette.custom.darkCharcoal,
            textAlign: "center",
          }}
        >
          Rightsbot: AI assistant for your rights
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          height: 48,
          width: "100%",
          display: "flex",
          alignItems: "center",
          mt: theme.spacing(3),
        }}
      >
        <Box
          sx={{
            width: "65%",
            mx: "auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {navLinks.map((link) => {
            const active = isActive(link.path);
            return (
              <Typography
                key={link.label}
                component={Link}
                to={link.path}
                sx={{
                  textDecoration: "none",
                  fontSize: 18,
                  fontWeight: active ? 400 : 700,
                  color: active ? theme.palette.custom.darkCharcoal : theme.palette.secondary.main,
                  px: theme.spacing(1.5),
                  py: active ? theme.spacing(0.5) : 0,
                  borderRadius: active ? theme.spacing(1) : 0,
                  backgroundColor: active ? theme.palette.custom.mintGreen : "transparent",
                  textAlign: "center",
                }}
              >
                {link.label}
              </Typography>
            );
          })}
          {isLoggedIn && (
            <Button
              onClick={onLogout}
              sx={{
                textTransform: "none",
                fontSize: 18,
                fontWeight: 700,
                color: theme.palette.secondary.main,
                backgroundColor: "transparent",
                border: "none",
                ml: theme.spacing(2),
              }}
            >
              Logout
            </Button>
          )}
          <Box sx={{ ml: theme.spacing(1) }}>{darkModeToggle}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NavBar;
